import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { useEffect } from "react";
import ErrorBoundary from "@/components/common/ErrorBoundary";

// Layout
import AdminLayout from "@/components/layout/AdminLayout";

// Pages
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import DestinationsPage from "@/pages/destinations/DestinationsPage";
import DestinationEditPage from "@/pages/destinations/DestinationEditPage";
import GuidesPage from "@/pages/guides/GuidesPage";
import GuideEditPage from "@/pages/guides/GuideEditPage";
import TripsPage from "@/pages/trips/TripsPage";
import TripEditPage from "@/pages/trips/TripEditPage";
import OrdersPage from "@/pages/orders/OrdersPage";
import OrderDetailPage from "@/pages/orders/OrderDetailPage";
import OrderKanbanPage from "@/pages/orders/OrderKanbanPage";
import NewOrderPage from "@/pages/orders/NewOrderPage";
import LeadsPage from "@/pages/leads/LeadsPage";
import LeadKanbanPage from "@/pages/leads/LeadKanbanPage";
import LeadDetailPage from "@/pages/leads/LeadDetailPage";
import InboxPage from "@/pages/inbox/InboxPage";
import CustomersPage from "@/pages/customers/CustomersPage";
import CustomerDetailPage from "@/pages/customers/CustomerDetailPage";
import NewCustomerPage from "@/pages/customers/NewCustomerPage";
import CampaignEditorPage from "@/pages/newsletter/CampaignEditorPage";
import StudioPage from "@/pages/newsletter/StudioPage";
import EditionEditorPage from "@/pages/newsletter/EditionEditorPage";
import ReviewsPage from "@/pages/reviews/ReviewsPage";
import ReviewEditPage from "@/pages/reviews/ReviewEditPage";
import MediaPage from "@/pages/MediaPage";
import HomepagePage from "@/pages/HomepagePage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import AutomationsPage from "@/pages/automations/AutomationsPage";
import UsersPage from "@/pages/users/UsersPage";
import TemplatesPage from "@/pages/templates/TemplatesPage";
import TemplateEditPage from "@/pages/templates/TemplateEditPage";
import JourneysPage from "@/pages/journeys/JourneysPage";
import JourneyEditPage from "@/pages/journeys/JourneyEditPage";
import JourneyEnrollmentsPage from "@/pages/journeys/JourneyEnrollmentsPage";
import AnalyticsPage from "@/pages/analytics/AnalyticsPage";
import SettingsPage from "@/pages/SettingsPage";
import VouchersPage from "@/pages/vouchers/VouchersPage";
import VoucherDetailPage from "@/pages/vouchers/VoucherDetailPage";
import QuizResultsPage from "@/pages/quiz/QuizResultsPage";
import ActivitiesPage from "@/pages/activities/ActivitiesPage";
import ActivityEditPage from "@/pages/activities/ActivityEditPage";
import BlogPostsPage from "@/pages/blog/BlogPostsPage";
import BlogPostEditPage from "@/pages/blog/BlogPostEditPage";
import DestinationTeamsPage from "@/pages/destination-teams/DestinationTeamsPage";
import DestinationTeamEditPage from "@/pages/destination-teams/DestinationTeamEditPage";
import PremiumUpgradesPage from "@/pages/premium-upgrades/PremiumUpgradesPage";
import PremiumUpgradeEditPage from "@/pages/premium-upgrades/PremiumUpgradeEditPage";
import FaqsPage from "@/pages/faqs/FaqsPage";
import FaqEditPage from "@/pages/faqs/FaqEditPage";
import SuppliersPage from "@/pages/suppliers/SuppliersPage";
import SupplierEditPage from "@/pages/suppliers/SupplierEditPage";
import SupplierComponentsPage from "@/pages/supplier-components/SupplierComponentsPage";
import SupplierComponentEditPage from "@/pages/supplier-components/SupplierComponentEditPage";
import ServiceQueuePage from "@/pages/services/ServiceQueuePage";
import FlightDeskPage from "@/pages/flights/FlightDeskPage";
import FlightAllotmentEditPage from "@/pages/flights/FlightAllotmentEditPage";
import CalendarPage from "@/pages/calendar/CalendarPage";
import InvoicesPage from "@/pages/invoices/InvoicesPage";
import FinancePage from "@/pages/finance/FinancePage";
import DocumentsPage from "@/pages/documents/DocumentsPage";
import DeparturesPage from "@/pages/departures/DeparturesPage";
import DepartureDetailPage from "@/pages/departures/DepartureDetailPage";
import SeasonPage from "@/pages/season/SeasonPage";
import SchedulePage from "@/pages/schedule/SchedulePage";
import ComplaintsPage from "@/pages/complaints/ComplaintsPage";
import AuditPage from "@/pages/audit/AuditPage";
import WikiPage from "@/pages/wiki/WikiPage";
import WikiEditPage from "@/pages/wiki/WikiEditPage";
import CountryKbPage from "@/pages/countryKb/CountryKbPage";
import CountryKbEditPage from "@/pages/countryKb/CountryKbEditPage";

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, checkAuth } = useAuthStore();

  useEffect(() => {
    // Only verify session on fresh page load (isLoading starts as true).
    // After login(), isLoading is already false and user is set — skip re-check
    // to avoid cross-origin cookie issues wiping the just-authenticated user.
    if (isLoading) {
      checkAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />

            {/* Destinations */}
            <Route path="destinations" element={<DestinationsPage />} />
            <Route path="destinations/:id" element={<DestinationEditPage />} />

            {/* Guides */}
            <Route path="guides" element={<GuidesPage />} />
            <Route path="guides/:id" element={<GuideEditPage />} />

            {/* Destination Teams */}
            <Route path="destination-teams" element={<DestinationTeamsPage />} />
            <Route path="destination-teams/:id" element={<DestinationTeamEditPage />} />

            {/* Trips */}
            <Route path="trips" element={<TripsPage />} />
            <Route path="trips/:id" element={<TripEditPage />} />

            {/* Inspirations */}
            <Route path="inspirations" element={<ActivitiesPage />} />
            <Route path="inspirations/:id" element={<ActivityEditPage />} />

            {/* FAQ */}
            <Route path="faqs" element={<FaqsPage />} />
            <Route path="faqs/:id" element={<FaqEditPage />} />

            {/* Blog */}
            <Route path="blog" element={<BlogPostsPage />} />
            <Route path="blog/:id" element={<BlogPostEditPage />} />

            {/* Premium Upgrades */}
            <Route path="premium-upgrades" element={<PremiumUpgradesPage />} />
            <Route path="premium-upgrades/:id" element={<PremiumUpgradeEditPage />} />

            {/* Inbox (omni-channel) */}
            <Route path="inbox" element={<InboxPage />} />

            {/* Leads (inquiry funnel) */}
            <Route path="leads" element={<LeadsPage />} />
            <Route path="leads/kanban" element={<LeadKanbanPage />} />
            <Route path="leads/:id" element={<LeadDetailPage />} />

            {/* Orders */}
            <Route path="orders" element={<OrdersPage />} />
            <Route path="orders/new" element={<NewOrderPage />} />
            <Route path="orders/kanban" element={<OrderKanbanPage />} />
            <Route path="orders/:id" element={<OrderDetailPage />} />

            {/* Customers */}
            <Route path="customers" element={<CustomersPage />} />
            <Route path="customers/new" element={<NewCustomerPage />} />
            <Route path="customers/:id" element={<CustomerDetailPage />} />

            {/* Newsletter — everything lives in the studio; old routes redirect (řez 3) */}
            <Route path="newsletter" element={<Navigate to="/newsletter-studio" replace />} />
            <Route path="newsletter/editor/:id" element={<CampaignEditorPage />} />
            <Route path="newsletter-studio" element={<StudioPage />} />
            <Route path="newsletter-studio/edition/:id" element={<EditionEditorPage />} />
            <Route
              path="segments"
              element={<Navigate to="/newsletter-studio?tab=segments" replace />}
            />

            {/* Automations */}
            <Route path="automations" element={<AutomationsPage />} />

            {/* Reviews */}
            <Route path="reviews" element={<ReviewsPage />} />
            <Route path="reviews/:id" element={<ReviewEditPage />} />

            {/* Media */}
            <Route path="media" element={<MediaPage />} />

            {/* Users */}
            <Route path="users" element={<UsersPage />} />

            {/* Templates */}
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="templates/:id" element={<TemplateEditPage />} />

            {/* Suppliers (operations) */}
            <Route path="suppliers" element={<SuppliersPage />} />
            <Route path="suppliers/:id" element={<SupplierEditPage />} />

            {/* Service catalog — priced components */}
            <Route path="supplier-components" element={<SupplierComponentsPage />} />
            <Route path="supplier-components/:id" element={<SupplierComponentEditPage />} />

            {/* Service queue — specialist by-kind worklist */}
            <Route path="services" element={<ServiceQueuePage />} />

            {/* Flight desk — group-flight allotments (B4.10, replaces GRPS LETENKY) */}
            <Route path="flights" element={<FlightDeskPage />} />
            <Route path="flights/:id" element={<FlightAllotmentEditPage />} />

            {/* Calendar — deadlines & tasks */}
            <Route path="calendar" element={<CalendarPage />} />

            {/* Season board — the whole quarter as a grid (B4.1.1, replaces SALE sheet) */}
            <Route path="season" element={<SeasonPage />} />
            {/* Guide schedule timeline (B4.2.2, replaces HRABE) */}
            <Route path="schedule" element={<SchedulePage />} />
            {/* Departures — group-departure velín (manifest, rooming, break-even) */}
            <Route path="departures" element={<DeparturesPage />} />
            <Route path="departures/:id" element={<DepartureDetailPage />} />
            <Route path="complaints" element={<ComplaintsPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="wiki" element={<WikiPage />} />
            <Route path="wiki/:id" element={<WikiEditPage />} />
            <Route path="country-kb" element={<CountryKbPage />} />
            <Route path="country-kb/:id" element={<CountryKbEditPage />} />

            {/* Documents — generated PDFs + e-signature */}
            <Route path="documents" element={<DocumentsPage />} />

            {/* Journeys — universal automation engine. Dynamic route only; the editor checks
                id === "new" (a static /new before /:id would null out id — see CLAUDE.md). */}
            <Route path="journeys" element={<JourneysPage />} />
            <Route path="journeys/:id/enrollments" element={<JourneyEnrollmentsPage />} />
            <Route path="journeys/:id" element={<JourneyEditPage />} />

            {/* Analytics */}
            <Route path="analytics" element={<AnalyticsPage />} />

            {/* Contact messages merged into the unified Inbox (vision §5.1) — redirect old links. */}
            <Route
              path="messages"
              element={<Navigate to="/inbox?channel=contact_form" replace />}
            />

            {/* Invoices + finance (margin / AR / AP / Pohoda) */}
            <Route path="invoices" element={<InvoicesPage />} />
            <Route path="finance" element={<FinancePage />} />

            {/* Vouchers */}
            <Route path="vouchers" element={<VouchersPage />} />
            <Route path="vouchers/:id" element={<VoucherDetailPage />} />

            {/* Quiz */}
            <Route path="quiz" element={<QuizResultsPage />} />

            {/* Homepage content */}
            <Route path="homepage" element={<HomepagePage />} />

            {/* Integrations (Google Drive backups) */}
            <Route path="integrations" element={<IntegrationsPage />} />

            {/* Settings */}
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
